/* eslint-disable max-lines */
import { useCameraDetails } from '@hakimo-ui/hakimo/data-access';
import { Frame } from '@hakimo-ui/hakimo/types';
import {
  BroadcastIcon,
  SpecialZones,
  SpecialZoneType,
  useImageLoader,
  ZoomableWrapper,
} from '@hakimo-ui/hakimo/ui-elements';
import { useFullscreen } from '@hakimo-ui/hakimo/util';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import AudioRecorder from '../../audio-recorder/AudioRecorder';
import { LiveViewSwitch } from '../../live-view/LiveViewSwitch';
import {
  useAlarmGroupCamera,
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import CamMonitorControls from '../controls/CamMonitorControls';
import { CameraFeedMode } from '../types';
import { getCameraZonePolygons } from '../utils';
import AlarmMedia from './AlarmMedia';
import FramesMedia from './FramesMedia';

interface Props {
  camId: string;
  globalCamMode: CameraFeedMode;
  visibleZoneTypes: Set<SpecialZoneType>;
}

export function CameraMonitorView(props: Props) {
  const { camId, globalCamMode, visibleZoneTypes } = props;
  const [mode, setMode] = useState<CameraFeedMode>(globalCamMode);
  const { alarmGroupId, product } = useAlarmGroupStatic();
  const { isSeekerPaused } = useAlarmGroupTime();
  const { camToDetailsMap, extraCamDetailsMap, camToAlarmTracker } =
    useAlarmGroupCamera();
  const { isBuffering } = useAlarmGroupPlayback();
  const isZoneOverlayEnabled = visibleZoneTypes.size > 0;
  const { data: cameraMetadata } = useCameraDetails(
    camId,
    undefined,
    isZoneOverlayEnabled
  );

  const cameraDetails = camToDetailsMap[camId] ?? extraCamDetailsMap[camId];
  const containerRef = useRef<HTMLDivElement>(null);
  const { isFullScreen, toggleFullScreen } = useFullscreen(containerRef);
  const { isTalkdownEnabled, cameraId, cameraName, cameraLiveStreamUrl } =
    cameraDetails || {};

  useEffect(() => {
    setMode(globalCamMode);
  }, [globalCamMode]);

  const [currentAlarmId, alarmIdsToLoad] = useMemo(() => {
    const cameraAlarmTracker = camToAlarmTracker.get(camId);
    if (!cameraAlarmTracker) {
      return ['', []];
    }
    const { showAlarmIds, preloadAlarmIds } = cameraAlarmTracker;
    const relevantAlarmIds = [
      ...new Set([...showAlarmIds, ...preloadAlarmIds]),
    ];
    return [cameraAlarmTracker?.currentAlarmId, relevantAlarmIds];
  }, [camId, camToAlarmTracker]);
  const { image: labellingImage } = useImageLoader(
    cameraMetadata?.imageUrl ?? ''
  );
  const specialZones = useMemo<SpecialZones>(
    () => ({
      polygons: getCameraZonePolygons(cameraMetadata, visibleZoneTypes),
      resolution: labellingImage
        ? {
            width: labellingImage.naturalWidth,
            height: labellingImage.naturalHeight,
          }
        : undefined,
    }),
    [cameraMetadata, visibleZoneTypes, labellingImage]
  );

  // Frames play as ONE continuous per-camera track on the shared clock,
  // instead of one element per alarm. Merge all of this camera's frames, sorted.
  const hasFrames = useMemo(
    () => (cameraDetails?.alarms ?? []).some((a) => a.media?.type === 'frames'),
    [cameraDetails]
  );
  const cameraFrames = useMemo<Frame[]>(() => {
    if (!hasFrames) return [];
    // Tag each frame with its source alarm so the face overlay stays scoped to
    // the alarm currently playing (matches the pre-merge per-alarm behavior).
    return (cameraDetails?.alarms ?? []).flatMap((alarm) =>
      (alarm.media?.frames ?? []).map((frame) => ({
        ...frame,
        alarmId: alarm.id,
        cameraName: alarm.cameraName,
        alarmType: alarm.alarm_type,
      }))
    );
  }, [hasFrames, cameraDetails]);

  const onChangeCamFeedMode = (val: CameraFeedMode) => setMode(val);

  if (!cameraDetails) {
    return (
      <div
        ref={containerRef}
        className="bg-ondark-bg-2 relative flex h-full w-full flex-grow items-center justify-center rounded-md"
      >
        <p className="text-ondark-text-1 text-sm">
          Camera details not available
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative flex h-full w-full flex-grow items-center justify-center rounded-md',
        mode !== CameraFeedMode.SPLIT && 'bg-ondark-bg-2'
      )}
    >
      {isTalkdownEnabled && (
        <div className="bg-onlight-bg-3/50 dark:bg-ondark-bg-3/50 text-ondark-text-1 absolute left-0 top-0 z-10 rounded-md">
          <AudioRecorder
            locAlarmId={alarmGroupId}
            cameraId={cameraId}
            cameraName={cameraName}
            product={product}
          />
        </div>
      )}
      <div
        className={clsx(
          mode === CameraFeedMode.ALARM_VIDEO
            ? 'block h-full w-full rounded-md'
            : mode === CameraFeedMode.SPLIT
            ? 'absolute bottom-0 left-0 right-[50.5%] top-0'
            : 'invisible h-0 w-0 opacity-0'
        )}
        onDoubleClick={toggleFullScreen}
      >
        {/* Frames decide the player. We no longer support a mix of frames +
        video in a single view (which location alarms used to): a camera's media
        is either scan-style frames or RG video, so frames presence alone
        selects FramesMedia vs the per-alarm video AlarmMedia. */}
        {hasFrames ? (
          <div className="relative h-full max-h-full w-full rounded-md">
            <ZoomableWrapper controlsTopOffset="1.25rem">
              <FramesMedia frames={cameraFrames} specialZones={specialZones} />
            </ZoomableWrapper>
          </div>
        ) : (
          alarmIdsToLoad.map((alarmId) => (
            <div
              key={alarmId}
              className={clsx(
                alarmId === currentAlarmId
                  ? 'h-full max-h-full'
                  : 'invisible h-0 w-0'
              )}
            >
              <AlarmMedia
                alarmId={alarmId}
                isPlaying={
                  !isSeekerPaused && !isBuffering && alarmId === currentAlarmId
                }
                isCurrentAlarm={alarmId === currentAlarmId}
                specialZones={specialZones}
              />
            </div>
          ))
        )}
      </div>
      {(mode === CameraFeedMode.LIVE || mode === CameraFeedMode.SPLIT) && (
        <div
          className={clsx(
            mode === CameraFeedMode.LIVE
              ? 'block h-full w-full rounded-md'
              : mode === CameraFeedMode.SPLIT
              ? 'absolute bottom-0 left-[50.5%] right-0 top-0 rounded-md'
              : 'invisible h-0 w-0 opacity-0'
          )}
          onDoubleClick={toggleFullScreen}
        >
          <LiveViewSwitch
            cameraId={cameraId}
            livestreamUrl={cameraLiveStreamUrl}
          />
        </div>
      )}
      {mode === CameraFeedMode.SPLIT && (
        <div className="text-ondark-text-1">
          <span className="bg-ondark-bg-2/70 absolute left-0 top-0 rounded-md p-1 text-xs">
            {cameraName} alarms
          </span>
          <span className="bg-ondark-bg-2/70 absolute left-1/2 top-0 flex items-center gap-1 rounded-md p-1 text-xs">
            <span>
              {cameraName} <span className="text-red-500">Live</span>
            </span>
            <span>
              <BroadcastIcon className="h-4 w-4 animate-pulse rounded-lg bg-red-500 fill-white" />
            </span>
          </span>
        </div>
      )}
      <CamMonitorControls
        mode={mode}
        cameraId={camId}
        isFullScreen={isFullScreen}
        onChangeCamFeedMode={onChangeCamFeedMode}
        toggleFullscreen={toggleFullScreen}
      />
    </div>
  );
}
